/**
 * Training — Workspace Activity.
 *
 * Rendered in the Subscription page's "Workspace Activity" tab for the training
 * platform. Backed by the dedicated training-activity API (ourteam-backend#3259):
 *     GET /subscriptions/training/activity-summary   → TrainingActivitySummary
 *     GET /subscriptions/training/activity-tracking   → TrainingActivityTracking
 * (roleplay sessions / campaigns / AIQM calls summary + a sessions-over-time
 * series and recent-sessions list, scoped to the caller's training workspace).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardListIcon, InfoIcon, Loader2Icon, MessageSquareTextIcon } from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from "recharts";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@component/card";
import { ChartContainer } from "@component/chart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@component/table";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import { Tooltip } from "@component/tooltip";
import { getTrainingActivitySummary, getTrainingActivityTracking } from "@service/subscription";
import {
    TrainingActivityGranularity,
    TrainingActivitySeriesPoint,
    TrainingActivitySummary,
    TrainingActivityTracking,
    TrainingRecentSession,
} from "@type/subscription";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

const STATUS_LABEL: Record<TrainingRecentSession["status"], string> = {
    completed: "Completed",
    in_progress: "In progress",
};

// Drop leading empty buckets so a workspace that only started recently doesn't
// render a long run of zero-value months (e.g. Jan–May empty, Jun = 1). Buckets
// from the first non-zero point onward are kept (mid-series gaps stay meaningful);
// an all-empty series returns [] and falls through to the empty state.
function trimLeadingEmptyBuckets(series: TrainingActivitySeriesPoint[]) {
    const firstNonZero = series.findIndex((p) => p.sessions > 0);
    return firstNonZero === -1 ? [] : series.slice(firstNonZero);
}

// 0–100 score → house semantic scale.
function scoreChipClass(score: number) {
    if (score >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (score >= 60) return "bg-blue-50 text-blue-700 border-blue-200";
    if (score >= 40) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-rose-50 text-rose-700 border-rose-200";
}

function formatDate(iso: string | null) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function BillingTrainingActivity() {
    const [period, setPeriod] = useState<TrainingActivityGranularity>("daily");

    const [summary, setSummary] = useState<TrainingActivitySummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryError, setSummaryError] = useState(false);

    const [tracking, setTracking] = useState<TrainingActivityTracking | null>(null);
    const [trackingLoading, setTrackingLoading] = useState(true);
    const [trackingError, setTrackingError] = useState(false);

    useEffect(() => {
        let active = true;
        setSummaryLoading(true);
        setSummaryError(false);
        getTrainingActivitySummary()
            .then((resp) => {
                if (!active) return;
                if (resp?.status === 200) {
                    setSummary(resp.data.data);
                    return;
                }
                setSummaryError(true);
            })
            .catch((err: unknown) => {
                if (active) setSummaryError(true);
                catchErrorLog(
                    `[training-activity] summary load failed: ${err instanceof Error ? err.message : String(err)}`,
                );
            })
            .finally(() => {
                if (active) setSummaryLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    // Tracking is refetched whenever `period` changes. Guard commits with a
    // request id so only the latest in-flight request may write state — quickly
    // switching Daily→Weekly→Monthly can never leave an older response's data
    // showing under the newly-selected tab.
    const trackingReqIdRef = useRef(0);

    const fetchTracking = useCallback(async (granularity: TrainingActivityGranularity) => {
        const requestId = ++trackingReqIdRef.current;
        setTrackingLoading(true);
        setTrackingError(false);
        try {
            const resp = await getTrainingActivityTracking(granularity);
            if (trackingReqIdRef.current !== requestId) return; // superseded — drop
            if (resp?.status === 200) {
                setTracking(resp.data.data);
            } else {
                setTrackingError(true);
            }
        } catch (err: unknown) {
            if (trackingReqIdRef.current !== requestId) return;
            setTrackingError(true);
            catchErrorLog(
                `[training-activity] tracking load failed: ${err instanceof Error ? err.message : String(err)}`,
            );
        } finally {
            if (trackingReqIdRef.current === requestId) setTrackingLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchTracking(period);
    }, [period, fetchTracking]);

    // Chart buckets with leading empty months trimmed off.
    const chartSeries = tracking ? trimLeadingEmptyBuckets(tracking.series) : [];

    return (
        <div className="space-y-6">
            {/* ── Activity Summary ── */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Activity Summary</h3>
                </div>
                {summaryLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[0, 1, 2].map((i) => (
                            <Card key={i}>
                                <CardContent className="p-6 space-y-3">
                                    <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
                                    <div className="h-8 w-16 bg-gray-100 rounded animate-pulse" />
                                    <div className="h-3 w-40 bg-gray-100 rounded animate-pulse" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : summaryError ? (
                    <Card className="p-6">
                        <p className="text-sm text-red-600">
                            We couldn't load your training activity right now. Please try again.
                        </p>
                    </Card>
                ) : summary ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <SummaryCard
                            title="Roleplay Sessions"
                            primaryLabel="Completed Per Billing Period"
                            primaryValue={summary.roleplay_sessions.total}
                            rows={[
                                { label: "Included in plan", value: summary.roleplay_sessions.included_in_plan },
                                { label: "Additional completed", value: summary.roleplay_sessions.additional, billed: true },
                            ]}
                        />
                        <SummaryCard
                            title="Active Campaigns"
                            primaryLabel="Currently Running"
                            primaryValue={summary.campaigns.active}
                            rows={[{ label: "Included in plan", value: summary.campaigns.included_in_plan }]}
                        />
                        <SummaryCard
                            title="AIQM Calls Analyzed"
                            primaryLabel="Analyzed Per Billing Period"
                            primaryValue={summary.aiqm_calls.total}
                            rows={[
                                { label: "Included in plan", value: summary.aiqm_calls.included_in_plan },
                                { label: "Additional analyzed", value: summary.aiqm_calls.additional, billed: true },
                            ]}
                        />
                    </div>
                ) : null}
            </div>

            {/* ── Activity Tracking ── */}
            <div>
                <div className="flex items-center justify-between mb-4 gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">Activity Tracking</h3>
                        <p className="text-xs text-muted-foreground mt-1">Roleplay sessions over time.</p>
                    </div>
                    <Tabs value={period} onValueChange={(v) => setPeriod(v as TrainingActivityGranularity)} className="w-auto">
                        <TabsList className="h-9">
                            <TabsTrigger value="daily" className="text-xs px-3 data-[state=active]:text-blue-600">Daily</TabsTrigger>
                            <TabsTrigger value="weekly" className="text-xs px-3 data-[state=active]:text-blue-600">Weekly</TabsTrigger>
                            <TabsTrigger value="monthly" className="text-xs px-3 data-[state=active]:text-blue-600">Monthly</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    <Card className="flex flex-col">
                        <CardHeader className="space-y-1">
                            <CardTitle className="text-base">Roleplay Sessions</CardTitle>
                            <CardDescription>
                                {period === "daily" && "Daily roleplay activity"}
                                {period === "weekly" && "Weekly roleplay activity"}
                                {period === "monthly" && "Monthly roleplay activity"}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {trackingLoading && !tracking ? (
                                <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
                                    <Loader2Icon className="h-4 w-4 animate-spin mr-2" />
                                    Loading activity…
                                </div>
                            ) : trackingError ? (
                                <div className="flex h-[280px] items-center justify-center">
                                    <p className="text-sm text-red-600">
                                        We couldn't load activity tracking right now. Please try again.
                                    </p>
                                </div>
                            ) : tracking && chartSeries.length > 0 ? (
                                <ChartContainer config={{}} className="h-[280px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartSeries as never[]} margin={{ top: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                            <XAxis dataKey="label" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                                            <YAxis className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                                            <Bar dataKey="sessions" fill="hsl(210, 100%, 65%)" radius={[4, 4, 0, 0]}>
                                                <LabelList
                                                    dataKey="sessions"
                                                    position="top"
                                                    style={{ fill: "hsl(var(--foreground))", fontSize: "12px", fontWeight: "500" }}
                                                />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartContainer>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 px-6">
                                    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                        <MessageSquareTextIcon className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <p className="text-center text-muted-foreground font-medium mb-2">
                                        No roleplay sessions during this period.
                                    </p>
                                    <p className="text-center text-sm text-muted-foreground/80">
                                        Activity will appear here once learners complete sessions.
                                    </p>
                                </div>
                            )}

                            {!trackingError && tracking && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-semibold mb-3">Recent Sessions</h4>
                                    {tracking.recent_sessions.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">No sessions yet.</p>
                                    ) : (
                                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-gray-50">
                                                        <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Learner</TableHead>
                                                        <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</TableHead>
                                                        <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Score</TableHead>
                                                        <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Status</TableHead>
                                                        <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Date</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {tracking.recent_sessions.map((s) => (
                                                        <TableRow key={s.id} className="hover:bg-gray-50 transition-colors">
                                                            <TableCell>
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                                                        <ClipboardListIcon className="w-4 h-4 text-blue-600" />
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-900">{s.learner_name || "—"}</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-sm text-gray-700">{s.campaign_name || "—"}</TableCell>
                                                            <TableCell className="text-center">
                                                                {s.score != null ? (
                                                                    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border", scoreChipClass(s.score))}>
                                                                        {s.score}/100
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-400">—</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-center">
                                                                <span className={cn(
                                                                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
                                                                    s.status === "completed"
                                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                                        : "bg-amber-50 text-amber-700 border-amber-200",
                                                                )}>
                                                                    {STATUS_LABEL[s.status]}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className="text-center text-sm text-gray-700">{formatDate(s.completed_at)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    title,
    primaryLabel,
    primaryValue,
    rows,
}: {
    title: string;
    primaryLabel: string;
    primaryValue: number;
    rows: { label: string; value: number; billed?: boolean }[];
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
                <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{primaryLabel}</p>
                    <span className="text-3xl font-bold">{primaryValue}</span>
                </div>
                <ul className="m-0 p-0 flex flex-col gap-1">
                    {rows.map((row) => (
                        <li key={row.label} className="flex items-start text-muted-foreground gap-2 text-sm m-0 p-0">
                            <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                            <span>{row.label}:</span>
                            {row.billed ? (
                                <div className="flex items-center gap-1">
                                    <Tooltip content="Additional usage will be billed at the end of the billing cycle.">
                                        <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                    </Tooltip>
                                    <span className="font-medium">{row.value}</span>
                                </div>
                            ) : (
                                <span className="font-medium">{row.value}</span>
                            )}
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}
